import { base, ecrireReglage, effacerReglage, lireReglage } from './db'
import { emettre } from './bus'
import { enfiler } from './file'
import { horlogeSysteme, type Horloge } from './horloge'
import { uuid } from './uuid'
import type {
  CodeSecteur,
  Espece,
  Observation,
  Patrouille,
  Signalement,
  TypeObservation,
  TypeSignalement,
} from '../domaine/types'

/*
  Écritures de terrain.

  Toutes suivent le même ordre, sans exception :
    1. écrire l'entité localement,
    2. enfiler l'événement de synchronisation,
    3. rendre la main à l'interface.

  Aucun `await fetch` sur ce chemin. Une saisie est confirmée parce qu'elle est
  écrite sur l'appareil, jamais parce qu'un serveur a répondu.
*/

export async function patrouilleActive(): Promise<Patrouille | null> {
  const id = await lireReglage('patrouille_active')
  if (!id) return null
  const db = await base()
  const p = await db.get('patrouilles', id)
  // Une patrouille clôturée n'est plus active, même si le réglage traîne.
  return p && p.fin === null ? p : null
}

export async function ouvrirPatrouille(
  secteur: CodeSecteur,
  equipe: string | null,
  horloge: Horloge = horlogeSysteme,
): Promise<Patrouille> {
  const patrouille: Patrouille = {
    id: uuid(),
    secteur,
    debut: horloge.iso(),
    fin: null,
    equipe: equipe?.trim() || null,
  }

  const db = await base()
  await db.put('patrouilles', patrouille)
  await ecrireReglage('patrouille_active', patrouille.id)

  await enfiler(
    patrouille.id,
    'patrouille.ouvrir',
    { secteur: patrouille.secteur, debut: patrouille.debut, equipe: patrouille.equipe },
    horloge,
  )

  emettre('patrouille-changee')
  return patrouille
}

export async function cloturerPatrouille(horloge: Horloge = horlogeSysteme): Promise<void> {
  const patrouille = await patrouilleActive()
  if (!patrouille) return

  const fin = horloge.iso()
  const db = await base()
  await db.put('patrouilles', { ...patrouille, fin })
  await effacerReglage('patrouille_active')

  // L'id de l'événement est celui de la patrouille : côté serveur, la clôture
  // est un UPDATE en COALESCE, donc rejouable sans écraser la première valeur.
  await enfiler(patrouille.id, 'patrouille.cloturer', { fin }, horloge)

  emettre('patrouille-changee')
}

/** Incrémente un compteur de +1. `espece` est obligatoire pour une ponte. */
export async function compter(
  type: TypeObservation,
  espece: Espece | null = null,
  horloge: Horloge = horlogeSysteme,
): Promise<Observation | null> {
  const patrouille = await patrouilleActive()
  if (!patrouille) return null

  if (type === 'ponte' && espece === null) {
    throw new Error('Une ponte exige une espèce.')
  }

  const observation: Observation = {
    id: uuid(),
    patrouille_id: patrouille.id,
    type,
    espece: type === 'ponte' ? espece : null,
    quantite: 1,
    ts: horloge.iso(),
  }

  const db = await base()
  await db.put('observations', observation)

  await enfiler(
    observation.id,
    'observation.creer',
    {
      patrouille_id: observation.patrouille_id,
      type: observation.type,
      espece: observation.espece,
      quantite: observation.quantite,
      ts: observation.ts,
    },
    horloge,
  )

  emettre('patrouille-changee')
  return observation
}

export type Comptes = Record<TypeObservation, number>

export async function comptesDe(patrouilleId: string): Promise<Comptes> {
  const db = await base()
  const observations = await db.getAllFromIndex('observations', 'par-patrouille', patrouilleId)

  const comptes: Comptes = {
    personne_sensibilisee: 0,
    ponte: 0,
    trace_sans_ponte: 0,
    emergence: 0,
  }
  for (const o of observations) {
    comptes[o.type] += o.quantite
  }
  return comptes
}

export async function creerSignalement(
  entree: {
    type: TypeSignalement
    secteur: CodeSecteur
    quantite: number | null
    note: string | null
    photo: Blob | null
  },
  horloge: Horloge = horlogeSysteme,
): Promise<Signalement> {
  const db = await base()
  let photoId: string | null = null

  if (entree.photo) {
    photoId = uuid()
    await db.put('photos', { id: photoId, blob: entree.photo, envoye: false })
    // La photo part dans son propre lot : un envoi lourd qui échoue ne doit
    // pas faire retomber les observations avec lui.
    await enfiler(photoId, 'photo.televerser', { mime: entree.photo.type || 'image/jpeg' }, horloge)
  }

  const signalement: Signalement = {
    id: uuid(),
    type: entree.type,
    secteur: entree.secteur,
    quantite: entree.quantite,
    note: entree.note?.trim() || null,
    photo_id: photoId,
    ts: horloge.iso(),
  }

  await db.put('signalements', signalement)
  await enfiler(
    signalement.id,
    'signalement.creer',
    {
      type: signalement.type,
      secteur: signalement.secteur,
      quantite: signalement.quantite,
      note: signalement.note,
      photo_id: signalement.photo_id,
      ts: signalement.ts,
    },
    horloge,
  )

  emettre('saison-changee')
  return signalement
}
